import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, LoginRequest, AuthResponse } from '../types';
import authService from '../services/authService';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<AuthResponse>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<AuthResponse | null>;
  isAdmin: () => boolean;
  hasRole: (role: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Initialize authentication state
    const initializeAuth = async () => {
      try {
        const currentUser = authService.getCurrentUser();
        if (currentUser && authService.getAccessToken()) {
          setUser(currentUser);
        } else {
          // Try to refresh token if we have a refresh token
          const refreshResult = await authService.refreshTokens();
          if (refreshResult) {
            setUser(refreshResult.user);
          }
        }
      } catch (error) {
        console.error('Failed to initialize authentication:', error);
      } finally {
        setIsLoading(false);
      }
    };

    // Listen for auth logout events from axios interceptor
    const handleAuthLogout = () => {
      setUser(null);
    };

    window.addEventListener('auth-logout', handleAuthLogout);

    initializeAuth();

    // Cleanup event listener
    return () => {
      window.removeEventListener('auth-logout', handleAuthLogout);
    };
  }, []);

  const login = async (credentials: LoginRequest): Promise<AuthResponse> => {
    try {
      const authResponse = await authService.login(credentials);
      setUser(authResponse.user);
      return authResponse;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await authService.logout();
      setUser(null);
    } catch (error) {
      console.error('Logout failed:', error);
      setUser(null); // Clear user even if logout request fails
    }
  };

  const refreshToken = async (): Promise<AuthResponse | null> => {
    try {
      const authResponse = await authService.refreshTokens();
      if (authResponse) {
        setUser(authResponse.user);
      } else {
        setUser(null);
      }
      return authResponse;
    } catch (error) {
      console.error('Token refresh failed:', error);
      setUser(null);
      return null;
    }
  };

  const isAdmin = (): boolean => {
    return authService.isAdmin();
  };

  const hasRole = (role: string): boolean => {
    return authService.hasRole(role);
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: authService.isAuthenticated(),
    isLoading,
    login,
    logout,
    refreshToken,
    isAdmin,
    hasRole,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;